/**
 * Visual score indicator component.
 *
 * Renders a horizontal bar showing a 1-5 score with:
 * - Color coding (green = 4-5, amber = 3, red = 1-2)
 * - Label and numeric score with qualitative tag
 * - Proportional fill width with smooth animation
 */

import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

interface ScoreBarProps {
  label: string;
  score: number | null;
  maxScore?: number;
}

function getScoreColor(score: number, theme: Theme): string {
  if (score >= 4) return theme.palette.success.main; // Green — strong
  if (score >= 3) return theme.palette.warning.main; // Amber — solid
  return theme.palette.error.main; // Red — needs work
}

function getScoreLabel(score: number, t: TFunction): string {
  const labels: Record<number, string> = {
    5: t("ScoreBar.exceptional"),
    4: t("ScoreBar.strong"),
    3: t("ScoreBar.solid"),
    2: t("ScoreBar.needsWork"),
    1: t("ScoreBar.offTrack"),
  };
  return labels[score] || "";
}

export default function ScoreBar({
  label,
  score,
  maxScore = 5,
}: ScoreBarProps) {
  const theme = useTheme();
  const { t } = useTranslation("common");

  if (score === null) {
    return (
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
          <Typography variant="body2" fontWeight={500}>
            {label}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            &mdash;
          </Typography>
        </Stack>
        <Box
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: "grey.100",
          }}
        />
      </Box>
    );
  }

  const pct = (score / maxScore) * 100;
  const color = getScoreColor(score, theme);
  const qualLabel = getScoreLabel(score, t);

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Typography variant="body2" fontWeight={500} sx={{ color }}>
            {score}/{maxScore}
          </Typography>
          {qualLabel && (
            <Typography variant="caption" sx={{ color, fontWeight: 500 }}>
              {qualLabel}
            </Typography>
          )}
        </Stack>
      </Stack>
      <Box
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: "grey.100",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: 4,
            bgcolor: color,
            transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      </Box>
    </Box>
  );
}
